import { useHover } from '@shared/hooks';
import { createProvider } from '@shared/react/createProvider';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import type { SidebarMenuItem } from './types';
import { isMenuActive } from './utils';

type ContextProps = {
  isMenuExpanded: boolean;
  isHovering: boolean;
  ref: React.ForwardedRef<HTMLDivElement>;
  toggleMenu: () => void;
  menuItem: SidebarMenuItem;
  isActive: boolean;
};
type Props = {
  menuItem: SidebarMenuItem;
};

const useValue = (props: Props): ContextProps => {
  const { menuItem } = props;
  const location = useLocation();
  const { ref, isHovering } = useHover<HTMLDivElement>();

  const hasSubmenuActive = menuItem?.children?.some((item) =>
    isMenuActive(location.pathname, item)
  );

  // Check if current location matches this menu group or its children
  const isMenuGroupActive = 
    (menuItem.url && isMenuActive(location.pathname, menuItem)) || hasSubmenuActive;

  const [isMenuExpanded, setIsExpanded] = useState<boolean>(!!hasSubmenuActive);

  useEffect(() => {
    if (isMenuGroupActive) {
      // If current location matches this menu group, keep it expanded
      setIsExpanded(true);
    } else {
      // If current location doesn't match this menu group, close it
      setIsExpanded(false);
    }
  }, [isMenuGroupActive, location.pathname]);

  const isActive =
    (!!menuItem.url && isMenuActive(location.pathname, menuItem)) || !!hasSubmenuActive;

  const toggleMenu = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  return {
    ref,
    isHovering,
    isMenuExpanded,
    toggleMenu,
    menuItem,
    isActive,
  };
};

useValue.__PROVIDER__ = 'src/components/features/SideBar/Menu/provider.tsx';
export const { Provider: MenuGroupProvider, useContext: useMenuGroupContext } =
  createProvider(useValue);
